import { isEmpty } from 'lodash';
import React, { useCallback, useMemo } from 'react';

import BaseScore from 'pages/Bulletin/components/BaseScore';
import { ChartData } from 'pages/Bulletin/types/chart-data';
import { HistoryOfChangesItem } from 'pages/Bulletin/types/history-of-changes';
import { isProtectedData } from 'pages/components/ProtectedData';
import toMeaningfulFixed from 'utils/to-meaningful-fixed';

interface Props {
    epss: {
        epss: number;
        percentile: number;
    }[];
}

const PRECISION = 5;

const Epss: React.FC<Props> = ({ epss }) => {
    const statistics = useCallback((data: HistoryOfChangesItem[]) => {
        if (data.every(({ changes }) => 'epss' in changes === false)) {
            return null;
        }
        return data.reduce<ChartData>((output, { version, published, changes }) => {
            const score = changes.epss
                ? Math.max(...changes.epss.map(({ epss }) => epss), 0)
                : output.at(-1)?.score ?? 0;
            output.push({ version, score, published });
            return output;
        }, []);
    }, []);

    const [head] = epss;
    const score = epss.reduce(
        (max, { epss, percentile }) => {
            switch (true) {
                case isProtectedData(epss) && !isProtectedData(max.epss):
                    return { epss, percentile };
                case !isProtectedData(epss) && isProtectedData(max.epss):
                    return max;
                case isProtectedData(epss) && isProtectedData(max.epss):
                    return String(epss).length > String(max.epss).length ? { epss, percentile } : max;
                default:
                    return epss > max.epss ? { epss, percentile } : max;
            }
        },
        {
            epss: head?.epss ?? 0,
            percentile: head?.percentile ?? 0
        }
    );

    const data = useMemo(
        () => [
            {
                score: isProtectedData(score.epss) ? score.epss : toMeaningfulFixed(score.epss, PRECISION),
                statistics,
                parts: {
                    percentile: isProtectedData(score.percentile)
                        ? score.percentile
                        : (score.percentile * 100).toFixed(1) + '%'
                }
            }
        ],
        [score.epss, score.percentile, statistics]
    );

    if (!Array.isArray(epss) || isEmpty(epss)) {
        return null;
    }

    return <BaseScore label="EPSS" data={data} precision={PRECISION} scale={0.1} />;
};

export default Epss;
